import { useEffect, useState } from 'react'

/** TinyMCE CDN version loaded globally (must match @tinymce/tinymce-react / Editor init). */
export const TINYMCE_CDN_VERSION = '8'

export function getTinyMceKey() {
  const key = window.__TINYMCE_KEY__ || localStorage.getItem('TINYMCE_KEY')
  return typeof key === 'string' ? key.trim() : ''
}

export function getTinyMceScriptSrc(version = TINYMCE_CDN_VERSION) {
  const key = getTinyMceKey()
  return key
    ? `https://cdn.tiny.cloud/1/${key}/tinymce/${version}/tinymce.min.js`
    : ''
}

export function applyTinyMceKeyFromSettings(settings) {
  const key = settings?.tinymceKey?.trim?.() || settings?.tinymceKey
  if (key) {
    window.__TINYMCE_KEY__ = key
    localStorage.setItem('TINYMCE_KEY', key)
  }
}

/** Inject Tiny Cloud script once key is on window.__TINYMCE_KEY__ (MultiVendor pattern). */
export function injectTinyMceCdn(version = TINYMCE_CDN_VERSION) {
  const key = getTinyMceKey()
  if (!key) return
  window.__TINYMCE_KEY__ = key

  const scriptSrc = getTinyMceScriptSrc(version)
  const existing = document.querySelector(`script[src="${scriptSrc}"]`)
  if (existing) {
    if (window.tinymce && typeof window.tinymce.init === 'function') {
      window.dispatchEvent(new CustomEvent('tinymce-loaded'))
    }
    return
  }

  document
    .querySelectorAll('script[src*="cdn.tiny.cloud/1/"]')
    .forEach((node) => node.remove())

  const script = document.createElement('script')
  script.src = scriptSrc
  script.referrerPolicy = 'origin'
  script.crossOrigin = 'anonymous'
  script.async = true
  script.onload = () => {
    window.dispatchEvent(new CustomEvent('tinymce-loaded'))
  }
  script.onerror = () => {
    console.error('TinyMCE CDN script failed to load:', scriptSrc)
  }
  document.head.appendChild(script)
}

export function useTinyMceApiKey() {
  const [apiKey, setApiKey] = useState(() => getTinyMceKey())

  useEffect(() => {
    const sync = () => {
      const key = getTinyMceKey()
      if (key) setApiKey(key)
    }
    sync()
    window.addEventListener('tinymce-loaded', sync)
    return () => window.removeEventListener('tinymce-loaded', sync)
  }, [])

  return apiKey
}

export function useTinyMceScriptReady(isReady, setIsReady) {
  useEffect(() => {
    if (isReady) return
    if (window.tinymce && typeof window.tinymce.init === 'function') {
      setIsReady(true)
      return
    }
    const onLoaded = () => {
      if (window.tinymce && typeof window.tinymce.init === 'function') {
        setIsReady(true)
      }
    }
    window.addEventListener('tinymce-loaded', onLoaded)
    injectTinyMceCdn()
    return () => window.removeEventListener('tinymce-loaded', onLoaded)
  }, [isReady, setIsReady])
}
